import React, {useState} from 'react';
import {Button, Grid, TextField, Typography} from '@material-ui/core';
import {makeStyles} from '@material-ui/core/styles';
import Api from '../../../../store/Api';
import {
    DetailedCheckResponse,
    DuplicateCheckLimits,
    DuplicateCheckParams,
    LlmConfig,
} from '../../../../store/models/DuplicateCheck';
import WeightsPanel from './WeightsPanel';
import DetailedResultView from './DetailedResultView';

const useStyles = makeStyles((theme) => ({
    errorText: {
        color: theme.palette.error.main,
    },
}));

interface SingleCheckFormProps {
    form: DuplicateCheckParams;
    onFormChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    maxTopN: number;
    weightsOpen: boolean;
    onWeightsToggle: () => void;
    weightValues: Record<string, number>;
    onWeightChange: (key: string, raw: string) => void;
    onWeightsReset: () => void;
    weightsPayload: Record<string, number>;
    onLimits: (limits: DuplicateCheckLimits) => void;
    llmConfig: LlmConfig | null;
    llmPreset: string | undefined;
    onLlmPresetChange: (preset: string) => void;
}

export default function SingleCheckForm(props: SingleCheckFormProps) {
    const classes = useStyles();
    const {
        form, onFormChange, maxTopN, weightsOpen, onWeightsToggle,
        weightValues, onWeightChange, onWeightsReset, weightsPayload, onLimits,
        llmConfig, llmPreset, onLlmPresetChange,
    } = props;

    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState<DetailedCheckResponse | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [runId, setRunId] = useState(0);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        setError(null);
        setResult(null);
        try {
            const res = await Api.duplicateCheckDetailed({...form, weights: weightsPayload});
            setResult(res);
            setRunId((id) => id + 1);
            if (res?.config?.limits) onLimits(res.config.limits);
        } catch (err: any) {
            setError(typeof err === 'string' ? err : 'Fehler beim API-Aufruf');
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <form onSubmit={handleSubmit} noValidate>
                <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                        <TextField fullWidth name="companyName" label="Firmenname"
                                   value={form.companyName ?? ''} onChange={onFormChange}/>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <TextField fullWidth name="address" label="Adresse"
                                   value={form.address ?? ''} onChange={onFormChange}/>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <TextField fullWidth name="phone" label="Telefon"
                                   value={form.phone ?? ''} onChange={onFormChange}/>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <TextField fullWidth name="website" label="Website"
                                   value={form.website ?? ''} onChange={onFormChange}/>
                    </Grid>
                    <Grid item xs={6} md={3}>
                        <TextField fullWidth name="lat" label="Breitengrad (lat)"
                                   type="number" inputProps={{step: 'any'}}
                                   value={form.lat ?? ''} onChange={onFormChange}/>
                    </Grid>
                    <Grid item xs={6} md={3}>
                        <TextField fullWidth name="lng" label="Längengrad (lng)"
                                   type="number" inputProps={{step: 'any'}}
                                   value={form.lng ?? ''} onChange={onFormChange}/>
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <TextField fullWidth name="googlePlacesId" label="Google Places ID"
                                   value={form.googlePlacesId ?? ''} onChange={onFormChange}/>
                    </Grid>
                    <Grid item xs={6} md={2}>
                        <TextField fullWidth name="topN" label="Top-N Kandidaten"
                                   type="number" inputProps={{min: 1, max: maxTopN, step: 1}}
                                   helperText={`max. ${maxTopN}`}
                                   value={form.topN ?? ''} onChange={onFormChange}/>
                    </Grid>
                    <Grid item xs={12}>
                        <WeightsPanel
                            open={weightsOpen}
                            onToggle={onWeightsToggle}
                            values={weightValues}
                            onChange={onWeightChange}
                            onReset={onWeightsReset}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <Button variant="contained" color="primary" type="submit" disabled={loading}>
                            {loading ? 'Prüfe...' : 'Prüfen'}
                        </Button>
                    </Grid>

                    {error && (
                        <Grid item xs={12}>
                            <Typography className={classes.errorText}>
                                {error}
                                {(error.includes('403') ||
                                    error.toLowerCase().includes('forbidden') ||
                                    error.toLowerCase().includes('access denied')) && (
                                    <> — fehlende ST_SUPERUSER-Berechtigung</>
                                )}
                            </Typography>
                        </Grid>
                    )}
                </Grid>
            </form>

            {result && (
                <DetailedResultView
                    key={runId}
                    result={result}
                    llmConfig={llmConfig}
                    llmPreset={llmPreset}
                    onLlmPresetChange={onLlmPresetChange}
                />
            )}
        </>
    );
}
